'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { AnswerUserType } from '~/model/AnswerUserType';
import type { QuestionReconstructedType } from '~/model/QuestionType';
import classNames from 'classnames';
import HTMLReactParser from 'html-react-parser';
import { useTranslations } from 'next-intl';
import { Element } from 'react-scroll';
import { MainColumn } from '@/app/components/MainColumn';
import { getResizedPictureUrl } from '@/app/utils/entities/media';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { ResizedImg } from '~/components/commons/ResizedImg/ResizedImg';
import { Zoom } from '~/components/commons/Zoom/Zoom';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { apiTypes } from '~/const/api';
import { getYoutubeIdFromUrl } from '~/utils/utils';
import { ChoiceTypeQuestion } from './Types/Choice/Choice';
import { RankingPictures } from './Types/Choice/RankingPictures';
import { GridTypeQuestion } from './Types/Grid';
import { IntegerTypeQuestion } from './Types/Integer';
import { RankingTypeQuestion } from './Types/Ranking';
import { RankingScaleTypeQuestion } from './Types/RankingScale';
import { ScaleTypeQuestion } from './Types/Scale';
import { SelectTypeQuestion } from './Types/Select';
import { StarRatingTypeQuestion } from './Types/StarRating';
import { TextTypeQuestion } from './Types/Text';
import { UploadTypeQuestion } from './Types/Upload';
import { VASTypeQuestion } from './Types/VAS';
import { checkIfHasPictureChoice } from './utils';

export const Question = ({
  answerUser,
  currentQuestion,
  disabled = false,
  langValue,
  handleChangeAnswer,
  handleLoading,
}: {
  answerUser: AnswerUserType;
  currentQuestion: QuestionReconstructedType;
  disabled?: boolean;
  langValue: string;
  handleChangeAnswer: (
    value: string,
    questionId: string,
    questionType: string,
    subQuestionId?: string,
  ) => void;
  handleLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  const t = useTranslations('GLOBAL');

  const {
    CHECK,
    CONDITIONAL,
    FILE,
    GRID,
    INTEGER,
    PICTURE,
    RADIO,
    RANKING,
    RANKING_SCALE,
    RANKING_PICTURE,
    SCALE,
    SELECT,
    STAR_RATING,
    TEXT,
    VAS1,
    VAS2,
    CHOICES_JAR,
  } = apiTypes;

  const {
    '@id': questionUri,
    choices_random_order,
    choices,
    description,
    id,
    label_max,
    label_middle,
    label_min,
    limit_max,
    limit_min,
    mandatory,
    media,
    other_answer,
    step,
    sub_questions,
    title,
    type,
    video_url,
  } = currentQuestion || {};

  const hasPictureChoice = checkIfHasPictureChoice(choices);

  return type
    ? (
        <div className="question" data-testid="question" data-cy="question">
          <Element
            aria-labelledby="questionTitle"
            name={`question-${id}`}
            role={`${type === RADIO ? 'radio' : ''}group`}
          >
            <BeardmanTalking
              title={(
                <label
                  lang={langValue}
                  htmlFor={`question-${id}`}
                  id="questionTitle"
                  data-cy="questionTitle"
                  data-testid="questionTitle"
                  className={classNames({
                    mandatory,
                  })}
                >
                  {title}
                </label>
              )}
              description={
                description && (
                  <div lang={langValue} data-testid="questionDescription">
                    {HTMLReactParser(description)}
                  </div>
                )
              }
            />
            <MainColumn column={journeyColumnTemplate}>
              {media && (
                <div className="control-media" data-testid="questionMedia">
                  <div className="control-media__picture">
                    <ResizedImg
                      alt=""
                      format="720x0"
                      priority
                      fill
                      sizes="(max-width: 576) 100vw, 500px"
                      src={media.content_url}
                    />
                    <Zoom
                      mainSrc={getResizedPictureUrl(media.content_url, '1600x0')}
                    />
                  </div>
                </div>
              )}
              {video_url && (
                <div
                  className="control-media control-media-video"
                  data-testid="questionVideo"
                >
                  <iframe
                    className="embed-responsive-item"
                    src={`//www.youtube.com/embed/${getYoutubeIdFromUrl(video_url)}?rel=0`}
                    allowFullScreen
                    title={`${t('VIDEO_TITLE')} "${title}"`}
                  />
                </div>
              )}
            </MainColumn>
            <div
              data-testid="form-fieldset"
              className={classNames('form-fieldset', {
                'form-fieldset-picture-choice': hasPictureChoice,
              })}
            >
              {type === TEXT && (
                <MainColumn column={journeyColumnTemplate}>
                  <TextTypeQuestion
                    answerUser={answerUser}
                    questionId={questionUri}
                    mandatory={mandatory}
                    handleChangeAnswer={handleChangeAnswer}
                    type={type}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {(type === VAS1 || type === VAS2) && (
                <MainColumn column={journeyColumnTemplate}>
                  <VASTypeQuestion
                    answerUser={answerUser}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    type={type}
                    limit_min={limit_min}
                    limit_max={limit_max}
                    label_min={label_min}
                    label_max={label_max}
                    langValue={langValue}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {type === SCALE && (
                <MainColumn column={journeyColumnTemplate}>
                  <ScaleTypeQuestion
                    answerUser={answerUser}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    step={step}
                    limit_min={limit_min}
                    limit_max={limit_max}
                    label_min={label_min}
                    label_middle={label_middle}
                    label_max={label_max}
                    langValue={langValue}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {type === RANKING_SCALE && (
                <MainColumn column={journeyColumnTemplate}>
                  <RankingScaleTypeQuestion
                    answerUser={answerUser}
                    answer_choices={choices}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    limit_min={limit_min}
                    limit_max={limit_max}
                    label_min={label_min}
                    label_max={label_max}
                    type={type}
                    langValue={langValue}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {[PICTURE, FILE].includes(type) && (
                <MainColumn column={journeyColumnTemplate}>
                  <UploadTypeQuestion
                    answerUser={answerUser}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    handleLoading={handleLoading}
                    type={type}
                  />
                </MainColumn>
              )}
              {type === GRID && (
                <MainColumn>
                  <GridTypeQuestion
                    answerUser={answerUser}
                    answer_choices={choices}
                    questionId={questionUri}
                    subQuestions={sub_questions}
                    handleChangeAnswer={handleChangeAnswer}
                    type={type}
                    langValue={langValue}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {type === RANKING && (
                <MainColumn column={journeyColumnTemplate}>
                  <RankingTypeQuestion
                    answerUser={answerUser}
                    answer_choices={choices}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    type={type}
                    choices_random_order={choices_random_order}
                    langValue={langValue}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {type === INTEGER && (
                <MainColumn column={journeyColumnTemplate}>
                  <IntegerTypeQuestion
                    answerUser={answerUser}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    type={type}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {type === STAR_RATING && (
                <MainColumn column={journeyColumnTemplate}>
                  <StarRatingTypeQuestion
                    answerUser={answerUser}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    subQuestions={sub_questions}
                    type={type}
                    langValue={langValue}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
              {(type === RADIO
                || type === CHECK
                || type === CHOICES_JAR
                || type === CONDITIONAL) && (
                <ChoiceTypeQuestion
                  answerUser={answerUser}
                  answerChoices={choices}
                  questionId={questionUri}
                  handleChangeAnswer={handleChangeAnswer}
                  type={type}
                  other_answer={other_answer}
                  choices_random_order={choices_random_order}
                  langValue={langValue}
                  disabled={disabled}
                />
              )}

              {type === RANKING_PICTURE && (
                <RankingPictures
                  answerUser={answerUser}
                  answerChoices={choices}
                  questionId={questionUri}
                  handleChangeAnswer={handleChangeAnswer}
                  type={type}
                  choicesRandomOrder={choices_random_order}
                  langValue={langValue}
                  disabled={disabled}
                />
              )}

              {type === SELECT && (
                <MainColumn column={journeyColumnTemplate}>
                  <SelectTypeQuestion
                    answerUser={answerUser}
                    answerChoices={choices}
                    questionId={questionUri}
                    handleChangeAnswer={handleChangeAnswer}
                    type={type}
                    langValue={langValue}
                    disabled={disabled}
                  />
                </MainColumn>
              )}
            </div>
          </Element>
        </div>
      )
    : null;
};
